/** Help: the topics in help.html, shown for this office and this account. tools/guide prints the same topics as the
 *  user guide PDF. F1 opens the topic for the screen you're on; ? opens the list. */
import helpSource from './help.html?raw';
import type { PrinterInfo } from '@imagesync/core';
import { can, canAdmin, hasModule, isAdmin, isGuest, displayName, role, S } from './state.js';
import { $, $maybe, ARROW, DOT, esc, money, plural, saveBlob, toast } from './util.js';
import { adminTab, isAdminOpen } from './admin.js';

interface Topic { id: string; title: string; group: string; needs: string; who: string; keys: string; related: string[]; node: HTMLElement; text?: string; textKey?: string }
const H = { topics: [] as Topic[], open: false, cur: null as string | null, q: '', lastFocus: null as HTMLElement | null };
const GROUPS = ['Start here', 'Finding records', 'Viewing pages', 'Printing and saving', 'Staff tools', 'Administration', 'Reference'];
let hooks: { allowedPrinters: () => PrinterInfo[]; canDownload: () => boolean } = { allowedPrinters: () => [], canDownload: () => false };

function load(): void {
  const t = document.createElement('template');
  t.innerHTML = helpSource;
  H.topics = Array.from(t.content.querySelectorAll<HTMLElement>('section[data-topic]')).map(s => ({
    id: s.dataset.topic!, title: s.dataset.title!, group: s.dataset.group ?? 'Reference', needs: s.dataset.needs ?? '', who: s.dataset.who ?? '',
    keys: (s.dataset.keys ?? '').toLowerCase(), related: (s.dataset.related ?? '').split(/\s+/).filter(Boolean), node: s }));
}
const WHO: Record<string, () => boolean> = { staff: () => can('originals'), records: () => can('editRecords'), import: () => can('importDocs'), delete: () => can('deleteRecords'), admin: () => canAdmin(),
  fulladmin: () => isAdmin(), download: () => hooks.canDownload(), guest: () => isGuest(), account: () => !isGuest() };
const hasMethod = (...m: string[]) => !!S.office?.indexes.some(i => m.includes(i.method));
const NEEDS: Record<string, () => boolean> = {
  // russell: any index with a key and a given-name column (the original's LMNRT, SimpleLMNRT and AlphaFirstInitial); the others by name
  russell: () => hasMethod('russell', 'simple-lmnrt', 'alpha-initial'), lmnrt: () => hasMethod('russell'), simplelmnrt: () => hasMethod('simple-lmnrt'),
  alpha: () => hasMethod('alpha'), alphainitial: () => hasMethod('alpha-initial'), cott: () => hasMethod('cott'), graves: () => hasMethod('graves'),
  estates: () => hasModule('estates'), bookpage: () => hasModule('bookpage'),
};
const whoOk = (w: string) => !w || w.split(/\s+/).some(x => !!WHO[x]?.());
const needsOk = (n: string) => !n || n.split(/\s+/).some(x => !!NEEDS[x]?.());
const topicOk = (t: Topic | undefined): t is Topic => !!t && needsOk(t.needs) && whoOk(t.who);
const topicById = (id: string) => H.topics.find(t => t.id === id);

function fill(key: string): string | undefined {
  const o = S.office, c = o?.contact, ps = hooks.allowedPrinters();
  return ({ office: o?.name, contactDesk: c?.desk, contactPhone: c?.phone, contactEmail: c?.email, contactHours: c?.hours,
    fee: o && o.feeCents ? money(o.feeCents) : undefined,
    printers: ps.length ? ps.map(p => p.label.replace(' · ', ' at the ')).join(' and ') : 'none, for this account' } as Record<string, string | undefined>)[key] || undefined;
}
function topicHTML(t: Topic): string {
  const node = t.node.cloneNode(true) as HTMLElement;
  node.querySelectorAll<HTMLElement>('[data-needs]').forEach(el => { if (!needsOk(el.dataset.needs!)) el.remove(); });
  node.querySelectorAll<HTMLElement>('[data-who]').forEach(el => { if (!whoOk(el.dataset.who!)) el.remove(); });
  node.querySelectorAll<HTMLElement>('[data-fill]').forEach(el => { const v = fill(el.dataset.fill!); if (v) el.textContent = v; });
  node.querySelectorAll<HTMLAnchorElement>('a[data-go]').forEach(a => {
    if (!topicOk(topicById(a.dataset.go!))) { a.remove(); return; }
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'linkish'; b.dataset.go = a.dataset.go!; b.textContent = a.textContent;
    a.replaceWith(b);
  });
  return node.innerHTML.replace(/\s*\(\s*\)/g, '');
}
function text(t: Topic): string {
  const key = (S.office?.slug ?? '') + '|' + (S.me?.user?.id ?? '') + '|' + JSON.stringify(S.me?.perms ?? {});
  if (t.textKey !== key) { const d = document.createElement('div'); d.innerHTML = topicHTML(t); t.text = (d.textContent ?? '').replace(/\s+/g, ' ').trim(); t.textKey = key; }
  return t.text!;
}

/** The topic for the screen you're on. */
export function helpContext(): string {
  if (isAdminOpen()) return ({ users: 'users', defaults: 'permissions', settings: 'settings', devices: 'agents', activity: 'activity' } as Record<string, string>)[adminTab()] ?? 'users';
  if ($maybe<HTMLDialogElement>('#importDlg')?.open) return 'import';
  const tab = S.office ? S.tab[S.office.slug] : undefined;
  if (tab === 'estates') return S.es.show ? 'estates' : 'casefile';
  if (tab === 'bookpage') return 'bookpage';
  const o = S.office;
  const m = o?.indexes.find(i => i.id === S.forms[o.slug]?.which)?.method;
  return m === 'cott' ? 'index-cott' : m === 'alpha' || m === 'graves' ? 'index-alpha' : 'index-russell';
}

function render(): void {
  const body = $('#helpBody'), q = H.q.trim().toLowerCase();
  if (q) { renderSearch(q); return; }
  if (H.cur) { renderTopic(H.cur); return; }
  const avail = H.topics.filter(topicOk), here = topicById(helpContext());
  body.innerHTML = (topicOk(here) ? '<div class="help-here"><span class="label">For this screen</span><button type="button" class="hlink strong" data-go="' + here.id + '">' + esc(here.title) + '</button></div>' : '') +
    '<p class="hint help-for">Topics for ' + esc(S.office?.name ?? '') + ', signed in as ' + esc(isGuest() ? 'the guest terminal' : displayName() + ' (' + role() + ')') + '.</p>' +
    GROUPS.map(g => {
      const ts = avail.filter(t => t.group === g);
      return ts.length ? '<section class="hgroup"><h3 class="eyebrow">' + esc(g) + '</h3><ul>' + ts.map(t => '<li><button type="button" class="hlink" data-go="' + t.id + '">' + esc(t.title) + '</button></li>').join('') + '</ul></section>' : '';
    }).join('');
}
function renderTopic(id: string): void {
  const t = topicById(id);
  if (!topicOk(t)) { H.cur = null; render(); return; }
  const rel = t.related.map(topicById).filter(topicOk);
  $('#helpBody').innerHTML = '<nav class="hcrumb" aria-label="Help"><button type="button" class="linkish" data-go="">All help topics</button><span aria-hidden="true">' + ARROW + '</span><span>' + esc(t.group) + '</span></nav>' +
    '<article class="htopic" aria-labelledby="ht-' + t.id + '"><h3 id="ht-' + t.id + '" tabindex="-1">' + esc(t.title) + '</h3>' + topicHTML(t) + '</article>' +
    (rel.length ? '<div class="hrel"><h4 class="eyebrow">Related</h4><ul>' + rel.map(r => '<li><button type="button" class="hlink" data-go="' + r.id + '">' + esc(r.title) + '</button></li>').join('') + '</ul></div>' : '');
  $('#helpBody').scrollTop = 0;
}
function renderSearch(q: string): void {
  const terms = q.split(/\s+/).filter(Boolean).slice(0, 8);
  const found = H.topics.filter(topicOk).map(t => {
    const title = t.title.toLowerCase(), body = text(t).toLowerCase();
    let score = 0;
    for (const w of terms) {
      const s = (title.includes(w) ? 6 : 0) + (t.keys.includes(w) ? 3 : 0) + (body.includes(w) ? 1 : 0);
      if (!s) return null;
      score += s;
    }
    return { t, score };
  }).filter((x): x is { t: Topic; score: number } => !!x).sort((a, b) => b.score - a.score);
  const rx = new RegExp('(' + terms.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')', 'gi');
  const mark = (s: string) => esc(s).replace(rx, '<mark>$1</mark>');
  const snippet = (t: Topic) => {
    const parts = text(t).split(/([.!?])\s+/), sentences: string[] = [];
    for (let i = 0; i < parts.length; i += 2) sentences.push(parts[i]! + (parts[i + 1] ?? ''));
    const hit = (sentences.find(s => terms.some(w => s.toLowerCase().includes(w))) ?? sentences[0] ?? '').trim();
    return mark(hit.length > 180 ? hit.slice(0, 177) + '…' : hit);
  };
  $('#helpBody').innerHTML = '<p class="hint" role="status">' + (found.length ? found.length + ' ' + plural(found.length, 'topic') + ' match' + (found.length === 1 ? 'es' : '') : 'No topics match. Try a single word, such as print, redact or TIFF.') + '</p>' +
    (found.length ? '<ul class="hres">' + found.map(f => '<li><button type="button" class="hlink" data-go="' + f.t.id + '"><span class="ht">' + mark(f.t.title) + '</span><span class="snip">' + snippet(f.t) + '</span></button></li>').join('') + '</ul>' : '');
}
function renderFoot(): void {
  const c = S.office?.contact;
  $('#helpContact').innerHTML = '<b>' + esc(c?.desk || S.office?.name || '') + '</b><br>' + esc(c?.phone ?? '') + (c?.hours ? ' ' + DOT + ' ' + esc(c.hours) : '');
}

export const isHelpOpen = () => H.open;
export function openHelp(id?: string): void {
  if (!H.topics.length) load();
  const p = $('#helpPanel');
  if (!H.open) H.lastFocus = document.activeElement as HTMLElement | null;
  H.open = true;
  p.hidden = false;
  $('#helpBtn').setAttribute('aria-expanded', 'true');
  H.q = ''; ($('#helpQ') as HTMLInputElement).value = '';
  H.cur = id && topicOk(topicById(id)) ? id : null;
  render(); renderFoot();
  const h = H.cur ? $maybe('#helpBody h3[tabindex]') : $maybe('#helpQ');
  h?.focus({ preventScroll: true });
}
export function closeHelp(): void {
  if (!H.open) return;
  H.open = false;
  $('#helpPanel').hidden = true;
  $('#helpBtn').setAttribute('aria-expanded', 'false');
  const f = H.lastFocus;
  H.lastFocus = null;
  if (f && document.contains(f) && !f.closest('[hidden]')) f.focus({ preventScroll: true });
}
/** After an office switch or a change to the account: show what now applies. */
export function refreshHelp(): void { if (H.open) { render(); renderFoot(); } }

async function downloadGuide(): Promise<void> {
  const r = await fetch('/help/ImageSync-User-Guide.pdf').catch(() => null);
  if (!r || !r.ok) { toast('The user guide couldn’t be loaded.', 'err'); return; }
  saveBlob(await r.blob(), 'ImageSync-User-Guide.pdf');
}

export function wireHelp(h: typeof hooks): void {
  hooks = h;
  $('#helpBtn').addEventListener('click', () => { if (H.open) closeHelp(); else openHelp(); });
  $('#helpClose').addEventListener('click', closeHelp);
  $('#helpQ').addEventListener('input', e => { H.q = (e.target as HTMLInputElement).value; render(); });
  $('#helpBody').addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-go]');
    if (!b) return;
    H.q = ''; ($('#helpQ') as HTMLInputElement).value = '';
    H.cur = b.dataset.go || null;
    render();
    (H.cur ? $maybe('#helpBody h3[tabindex]') : $maybe('#helpQ'))?.focus({ preventScroll: true });
  });
  $('#helpGuide').addEventListener('click', () => void downloadGuide());
  document.addEventListener('click', e => {
    const q = (e.target as HTMLElement).closest<HTMLElement>('.help-q');
    if (!q) return;
    let id = q.dataset.help ?? '';
    if (id === 'auto-search') { const tab = S.office ? S.tab[S.office.slug] : 'index'; id = tab === 'estates' ? 'estates' : tab === 'bookpage' ? 'bookpage' : helpContext(); }
    if (id === 'auto-admin') id = helpContext();
    const d = q.closest('dialog');   // a modal dialog makes the rest of the page inert, so close it first
    if (d) d.close();
    openHelp(id);
  });
}
